import { type Mesh, MeshBuilder, Vector3 } from "@babylonjs/core";
import { HeroId, Slot, hv, pk, zk } from "@rivalwatch/shared";
import { baked } from "../../audio/bake.ts";
import * as loops from "../../audio/cues/loops.ts";
import type { Cue } from "../../audio/engine.ts";
import type { Theme } from "../../audio/theory.ts";
import { bell, click, drive, fm, later, noise, pluck, thump, tone, trim, whoosh } from "../../audio/synth.ts";
import { glow } from "../../game/materials.ts";
import type { ClientHero, ProjectileFrame, ZoneFrame } from "../types.ts";

const V = { ultDash: 0, veil: 1 } as const;
const PK = { Dagger: pk(HeroId.CloakSwagger, 0), Terror: pk(HeroId.CloakSwagger, 1) } as const;
const ZK = { Storm: zk(HeroId.CloakSwagger, 0), BondTrail: zk(HeroId.CloakSwagger, 1) } as const;
const hz = (semi: number, base = 293.66) => base * 2 ** (semi / 12);

interface Gear {
  cloak: Mesh;
  hoodGlow: Mesh;
  daggerL: Mesh;
  daggerR: Mesh;
  lightFan: Mesh;
  shadow: Mesh;
  trail: Mesh;
}

const lightNeedle: Cue = baked(
  "fire:cloak-swagger:light-needle-polish",
  () =>
    trim(13, (o) => {
      click(o, 0.2, 5600, 0.003);
      bell(o, hz(24), 0.12, 0.4);
      whoosh(o, 0.16, 1700, 4600, 0.12);
      return 0.25;
    }),
  { variants: 5, jitter: 0.012 },
);
const nightNeedle: Cue = baked(
  "fire:cloak-swagger:night-needle-polish",
  () =>
    trim(4.8, (o) => {
      const d = drive(o, 2.1);
      thump(d, 0.2, 100, 44, 0.12);
      fm(d, { f: hz(-5), ratio: 1.5, index: 3.2, index1: 0.4, gain: 0.1, d: 0.18 });
      noise(d, { color: "pink", type: "lowpass", f: 1300, f1: 260, gain: 0.18, d: 0.16 });
      return 0.32;
    }),
  { variants: 5, jitter: 0.012 },
);
const daggerRain: Cue = trim(9.9, (o) => {
  for (let i = 0; i < 5; i++) bell(later(o, i * 0.035), hz(19 + (i % 2) * 5), 0.1, 0.38);
  noise(o, { color: "pink", type: "bandpass", f: 1200, f1: 3400, gain: 0.22, a: 0.08, d: 0.45 });
  return 0.85;
});
const dimDoor: Cue = trim(3.6, (o) => {
  thump(o, 0.32, 85, 34, 0.26);
  whoosh(o, 0.3, 420, 1600, 0.5);
  fm(o, { f: hz(-12), ratio: 1.25, index: 4, index1: 0.6, gain: 0.13, d: 0.45 });
  return 0.9;
});
const brightVeil: Cue = trim(14.4, (o) => {
  bell(o, hz(28), 0.2, 0.44);
  whoosh(o, 0.2, 1400, 4000, 0.22);
  return 0.55;
});
const tradePlaces: Cue = trim(14.6, (o) => {
  tone(o, { f: hz(7), f1: hz(-5), glide: 0.24, gain: 0.08, d: 0.28 });
  bell(later(o, 0.08), hz(19), 0.18, 0.35);
  return 0.55;
});
const foxtrot: Cue = trim(3.8, (o) => {
  thump(o, 0.42, 95, 38, 0.25);
  [0, 6, 7, 12].forEach((s, i) => bell(later(o, i * 0.055), hz(s + 12), 0.18, 0.5));
  whoosh(o, 0.35, 600, 3200, 0.55);
  return 1.2;
});
const cloakTheme = {
  bpm: 116,
  octave: 0,
  voice: pluck,
  double: (o, f, d, v) => void bell(later(o, 0.015), f * 2, d, v * 0.38),
  notes: [
    [0, 0.5],
    [6, 0.5],
    [7, 1],
    [2, 0.5],
    [12, 1.5],
  ],
} satisfies Theme;

const hero: ClientHero<Gear> = {
  id: HeroId.CloakSwagger,
  bulk: 1.02,
  shape: { head: 1.05, torso: 1.05 },
  forms: [{ shape: { head: 1.05, torso: 1.05 }, bulk: 1.04 }],
  gear(g) {
    const { scene, R, H, rig, add, addWorld, glow: gglow, toon } = g;
    const cloak = add(MeshBuilder.CreateBox("shadow-cloak", { width: R * 1.9, height: H * 0.7, depth: 0.055 }, scene), rig.torso, toon("#050510"));
    cloak.position.set(0, -H * 0.18, R * 0.72);
    const hoodGlow = add(MeshBuilder.CreateTorus("hood-crescent", { diameter: R * 1.05, thickness: 0.035, tessellation: 24 }, scene), rig.head, gglow("#fff1a6", 0.55));
    hoodGlow.rotation.x = Math.PI / 2;
    hoodGlow.position.z = -R * 0.48;
    const daggerL = add(MeshBuilder.CreateCylinder("light-dagger-l", { diameterTop: 0.02, diameterBottom: 0.09, height: 0.48, tessellation: 6 }, scene), rig.handL, gglow("#fff1a6", 0.95));
    daggerL.rotation.x = Math.PI / 2;
    daggerL.position.z = -0.2;
    const daggerR = add(MeshBuilder.CreateCylinder("light-dagger-r", { diameterTop: 0.02, diameterBottom: 0.09, height: 0.48, tessellation: 6 }, scene), rig.handR, gglow("#fff1a6", 0.95));
    daggerR.rotation.x = Math.PI / 2;
    daggerR.position.z = -0.2;
    const lightFan = add(MeshBuilder.CreateTorus("swagger-light-fan", { diameter: 0.55, thickness: 0.025, tessellation: 28 }, scene), rig.handR, gglow("#fff1a6", 0.45));
    lightFan.rotation.x = Math.PI / 2;
    lightFan.position.z = -0.18;
    const shadow = add(MeshBuilder.CreateSphere("shadow-core", { diameter: 0.42, segments: 12 }, scene), rig.torso, gglow("#4b2d8f", 0.62));
    shadow.position.z = -R * 0.45;
    const trail = addWorld(MeshBuilder.CreateTorus("foxtrot-ring", { diameter: 8, thickness: 0.06, tessellation: 48 }, scene), gglow("#d8c7ff", 0.24));
    trail.setEnabled(false);
    return { cloak, hoodGlow, daggerL, daggerR, lightFan, shadow, trail };
  },
  animate(gear, f, rig) {
    const cloakForm = f.form === 1;
    gear.cloak.setEnabled(cloakForm);
    gear.shadow.setEnabled(cloakForm);
    gear.daggerL.setEnabled(!cloakForm);
    gear.daggerR.setEnabled(!cloakForm);
    gear.lightFan.setEnabled(!cloakForm);
    gear.hoodGlow.rotation.z = f.t * (cloakForm ? -0.8 : 0.8);
    gear.shadow.scaling.setAll(cloakForm ? 1 + Math.sin(f.t * 5) * 0.08 : 0.01);
    gear.trail.setEnabled(f.ultActive);
    gear.trail.position.set(f.pos.x, f.pos.y + 0.05, f.pos.z);
    gear.trail.scaling.setAll(1 + Math.sin(f.t * 3) * 0.05);
    if (rig.parts.head) rig.parts.head.visibility = cloakForm ? 0.58 : 1;
    return cloakForm ? "holding-left" : "holding-both";
  },
  hud(c, eid) {
    const cloak = c.Player.form[eid] === 1;
    return [
      { label: cloak ? "Cloak" : "Swagger", value: cloak ? 1 : 0, color: cloak ? "#4b2d8f" : "#fff7ba" },
      { label: "Foxtrot dashes", value: hv(c, eid, V.ultDash) / 4, color: "#d8c7ff" },
    ];
  },
  projectiles: {
    [PK.Dagger]: (scene, f: ProjectileFrame) => {
      const dagger = MeshBuilder.CreateCylinder("swagger-dagger", { diameterTop: 0.02, diameterBottom: 0.12, height: 0.58, tessellation: 6 }, scene);
      dagger.material = glow(scene, "#fff1a6", 0.88);
      dagger.position.set(f.pos.x, f.pos.y, f.pos.z);
      return {
        gear: dagger,
        update(m: Mesh, frame) {
          m.position.set(frame.pos.x, frame.pos.y, frame.pos.z);
          m.lookAt(new Vector3(frame.pos.x + frame.dir.x, frame.pos.y + frame.dir.y, frame.pos.z + frame.dir.z));
          m.rotation.x += Math.PI / 2;
        },
        dispose: () => dagger.dispose(),
      };
    },
    [PK.Terror]: (scene, f: ProjectileFrame) => {
      const terror = MeshBuilder.CreateSphere("cloak-terror", { diameter: 0.42, segments: 10 }, scene);
      terror.material = glow(scene, "#4b2d8f", 0.78);
      terror.position.set(f.pos.x, f.pos.y, f.pos.z);
      return {
        gear: terror,
        update(m: Mesh, frame) {
          m.position.set(frame.pos.x, frame.pos.y, frame.pos.z);
          m.scaling.setAll(1 + Math.sin(frame.age * 14) * 0.1);
        },
        dispose: () => terror.dispose(),
      };
    },
  },
  zones: {
    [ZK.Storm]: (scene, f: ZoneFrame) => {
      const ring = MeshBuilder.CreateTorus("dagger-storm-ring", { diameter: f.radius * 2, thickness: 0.05, tessellation: 64 }, scene);
      ring.material = glow(scene, "#fff1a6", 0.25);
      ring.position.set(f.pos.x, f.pos.y + 0.06, f.pos.z);
      return {
        gear: ring,
        update(m: Mesh, frame) {
          m.position.set(frame.pos.x, frame.pos.y + 0.06, frame.pos.z);
          m.rotation.y += frame.dt * 2;
        },
        dispose: () => ring.dispose(),
      };
    },
    [ZK.BondTrail]: (scene, f: ZoneFrame) => {
      const ring = MeshBuilder.CreateTorus("foxtrot-trail-ring", { diameter: f.radius * 2, thickness: 0.06, tessellation: 48 }, scene);
      ring.material = glow(scene, "#d8c7ff", 0.24);
      ring.position.set(f.pos.x, f.pos.y + 0.07, f.pos.z);
      return {
        gear: ring,
        update(m: Mesh, frame) {
          m.position.set(frame.pos.x, frame.pos.y + 0.07, frame.pos.z);
          m.scaling.setAll(0.9 + Math.sin(frame.age * 8) * 0.05);
        },
        dispose: () => ring.dispose(),
      };
    },
  },
  icons: {
    [Slot.Primary]: "M4 13l7-7 2 5 7-5-7 12-2-5-7 5z",
    [Slot.Secondary]: "M5 16c5-8 9-8 14 0M6 18h12",
    [Slot.Ability1]: "M8 5h8M12 5v14M7 15l5 4 5-4",
    [Slot.Ability2]: "M4 12c4-5 12-5 16 0c-4 5-12 5-16 0z",
    [Slot.Ultimate]: "M5 12c3-5 11-5 14 0c-3 5-11 5-14 0zM12 4v16",
    passive: "M6 6c5 2 7 2 12 0M6 18c5-2 7-2 12 0",
  },
  audio: {
    weight: "mid",
    fire: (ctx) => ({ key: ctx?.form === 1 ? "fire:cloak-swagger:night-needle-polish" : "fire:cloak-swagger:light-needle-polish", cue: ctx?.form === 1 ? nightNeedle : lightNeedle }),
    ability: { [Slot.Secondary]: daggerRain, [Slot.Ability2]: brightVeil, [Slot.Ability1]: tradePlaces, [Slot.Ultimate]: foxtrot },
    abilityForms: { 1: { [Slot.Secondary]: dimDoor, [Slot.Ability2]: dimDoor, [Slot.Ability1]: tradePlaces, [Slot.Ultimate]: foxtrot } },
    formChange: { 0: brightVeil, 1: dimDoor },
    theme: cloakTheme,
    themeTrim: { heroic: 2.8, hostile: -6, tease: 9.3 },
    flight: { [PK.Dagger]: loops.flight("orb"), [PK.Terror]: loops.flight("orb") },
    zoneLoops: { [ZK.Storm]: { build: loops.bioticField, gain: 0.8, range: 30 }, [ZK.BondTrail]: { build: loops.portal, gain: 0.65, range: 34 } },
  },
};

export default hero;
